/**
 * MSI3StandalonePage - Medical SuperIntelligence³ about page, standalone layout.
 * No menu column; top bar uses the same split-flap display as the splash (CareMapSplitFlap).
 * Back link returns to the page that linked here (location.state.from) or home.
 * See TOP_OF_PAGE_SPLITFLAP_BY_ROLE.md (no menu variant, identical to splash flap).
 */
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Box, AppBar, Toolbar, Typography, Paper, IconButton, useTheme, useMediaQuery } from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import CareMapSplitFlap from "../splash/CareMapSplitFlap";

/** Match MapPage CareMap bar (compact flap + tagline). */
const TOOLBAR_HEIGHT_MSI3_WIDE =
  "max(62px, calc(1.45 * (3px + max(clamp(22px, 2.85vw, 38px), clamp(14px, 2vw, 24px))) + clamp(26px, 3.4vw, 34px)))";
const TOOLBAR_HEIGHT_MSI3_NARROW =
  "max(84px, calc(7px + 2 * clamp(9px, 2.25vw, 17px) + clamp(11px, 3.1vw, 20px) + clamp(28px, 4.8vw, 42px)))";

const MSI3_BAR_COLOR = "primary.main";

function MSI3FlapPortal({ toolbarHeight }) {
  const [mountNode, setMountNode] = useState(null);
  useEffect(() => {
    const el = document.createElement("div");
    el.setAttribute("data-msi3-flap-portal", "true");
    document.body.appendChild(el);
    setMountNode(el);
    return () => document.body.removeChild(el);
  }, []);

  const content = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: toolbarHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1200,
        pointerEvents: "none",
      }}
    >
      <CareMapSplitFlap />
    </div>
  );

  if (!mountNode) return null;
  return createPortal(content, mountNode);
}

const MSI3StandalonePage = () => {
  const theme = useTheme();
  const narrowLayout = useMediaQuery(theme.breakpoints.down("md"));
  const toolbarHeight = narrowLayout ? TOOLBAR_HEIGHT_MSI3_NARROW : TOOLBAR_HEIGHT_MSI3_WIDE;
  const toolbarSxWithFlap = {
    minHeight: toolbarHeight,
    height: toolbarHeight,
    paddingTop: 0,
    paddingBottom: 0,
    alignItems: "center",
    boxSizing: "border-box",
  };

  const location = useLocation();
  const navigate = useNavigate();
  const fromPath = location.state?.from;

  const handleBack = () => {
    if (fromPath) {
      navigate(fromPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          maxWidth: "100%",
          backgroundColor: MSI3_BAR_COLOR,
          background: MSI3_BAR_COLOR,
          boxShadow: "none",
        }}
      >
        <Toolbar sx={{ ...toolbarSxWithFlap, justifyContent: "flex-start", pl: 1 }}>
          {fromPath ? (
            <IconButton
              component={Link}
              to={fromPath}
              replace
              aria-label="Go back"
              sx={{ color: "common.white", pointerEvents: "auto" }}
            >
              <ArrowBackRounded />
            </IconButton>
          ) : (
            <IconButton onClick={handleBack} aria-label="Go back" sx={{ color: "common.white", pointerEvents: "auto" }}>
              <ArrowBackRounded />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      <MSI3FlapPortal toolbarHeight={toolbarHeight} />
      <Box
        sx={{
          paddingTop: toolbarHeight,
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100%",
          bgcolor: "background.default",
          overflow: "auto",
        }}
      >
        <Box sx={{ maxWidth: 720, mx: "auto", px: 2, py: 3, mb: 4 }}>
          <Typography
            variant="body2"
            component="div"
            sx={{ mb: 2, display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <ArrowBackRounded sx={{ fontSize: 18 }} />
            {fromPath ? (
              <Link to={fromPath} replace style={{ color: "inherit", textDecoration: "none" }}>
                Back
              </Link>
            ) : (
              <Box component="button" type="button" onClick={handleBack} sx={{ background: "none", border: "none", cursor: "pointer", font: "inherit", color: "inherit", p: 0 }}>
                Back
              </Box>
            )}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ mt: { xs: 1, sm: 2 }, mb: 1 }}>
              Medical SuperIntelligence<sup>3</sup>
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Human aspiration, not a machine
            </Typography>
          </Box>

          <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720 }}>
            <Typography variant="body1" paragraph>
              Medical SuperIntelligence is a human aspiration, not a machine.
            </Typography>
            <Typography variant="body1" paragraph>
              Intelligence — genuine intelligence in health care — is a uniquely human capacity. It encompasses not just computation and pattern recognition, but judgment, wisdom, presence, moral courage, compassion, and the ability to hold another person&apos;s suffering with grace. These are not features that can be artificially replicated, and they are what clinical intelligence actually consists of.
            </Typography>
            <Typography variant="body1" paragraph>
              Artificial intelligence is, by definition, artificial. It is an extraordinarily powerful tool — capable of processing decades of health data in seconds, detecting patterns invisible to the unaided eye, eliminating documentation burden, and supporting clinical decision-making at scale. But it is a tool. It does not understand. It does not care. It does not heal.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 600 }}>
              MSI³ is built on this distinction.
            </Typography>
            <Typography variant="body1" paragraph>
              The platform&apos;s purpose is not to replace clinical intelligence — it is to amplify it. By absorbing the computational labor that currently consumes nearly half of a physician&apos;s working day, MSI³ gives clinicians back the cognitive and emotional bandwidth to practice at the full depth of human capability. The patient&apos;s MasterChart extends this further by creating continuity of care — facilitating universal access to clinical documentation by every provider, regardless of EHR or institutional affiliation.
            </Typography>
            <Typography variant="body1" paragraph>
              Medical SuperIntelligence³, then, is the aspiration of the <strong>patient&apos;s</strong> visit with a <strong>clinician</strong>, and it can be enhanced by the <strong>facility</strong>. The &quot;3&quot; reflects three distinct roles: patient, clinician, and facility. Each functions independently for its respective user. Together, they create the potential for an exponential leap forward in the quality of medical care.
            </Typography>
            <Typography variant="body1" paragraph>
              It is what emerges in the encounter between a clinician and a patient when artificial intelligence handles what machines do well, freeing humans to bring everything that only humans can bring. The &quot;super&quot; in superintelligence doesn&apos;t describe the AI. It describes what human intelligence is capable of when it is no longer buried under information overload and administrative burden — when the clinician is finally free to see, to understand, to help.
            </Typography>
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default MSI3StandalonePage;
